<template>
    <div class="bonusomat-spin-automat" :class="additionalClass">
        <template v-if="iconsUrls.length">
            <BonusomatAutomatCylinder
                v-for="(winIcon, index) in winIcons"
                :key="index"
                class="bonusomat-spin-automat__cylinder"
                :class="`bonusomat-spin-automat__cylinder--${index}`"
                :icons-urls="iconsUrls"
                :state="state"
                :win-icon="winIcon"
                :spin-end-delay="350 * index"
                @spin-end="cylinderSpinEndHandler"
            />
        </template>

        <video
            v-show="showDefaultAnimation"
            ref="defaultAnimationRef"
            alt="Automat je aktivní"
            loop
            muted
            constrols="false"
            playsinline
            autoplay
            preload="auto"
            class="bonusomat-spin-automat__video"
        >
            <source v-if="isMovSupported" :src="bonImgUrl('automat/automat-active', 'mov')" type="video/quicktime" />
            <source :src="bonImgUrl('automat/automat-active', 'webm')" type="video/webm" />
        </video>

        <video
            v-show="showRollingAnimation"
            ref="rollingAnimationRef"
            alt="Automat se točí"
            muted
            constrols="false"
            playsinline
            preload="auto"
            class="bonusomat-spin-automat__video"
        >
            <source v-if="isMovSupported" :src="automatRollingMovSrc" type="video/quicktime" />
            <source :src="automatRollingWebmSrc" type="video/webm" />
        </video>

        <video
            v-show="showWinningAnimation"
            ref="winningAnimationRef"
            alt="Automat vyhrává"
            loop
            muted
            constrols="false"
            playsinline
            preload="auto"
            class="bonusomat-spin-automat__video"
        >
            <source v-if="isMovSupported" :src="automatWinMovSrc" type="video/quicktime" />
            <source :src="automatWinWebmSrc" type="video/webm" />
        </video>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watchEffect } from "vue";
import { Loader } from "@sazka/design-system/src/loader/loader";
import { useNgSession } from "@sazka/ng/ng-session";
import { MediaPreloader } from "@sazka/utils/media-preloader";
import { ResponsiveUtils } from "@sazka/utils/responsive-utils";
import BonusomatAutomatCylinder from "./bonusomat-automat-cylinder.vue";
import { fetchAutomatCylinderImages } from "../../../api/bonusomat-player-data-api";
import { SpinAutomatState } from "../../../models/bonusomat-spin-types";
import { bonImgUrl } from "../../../utils/image-src";

const emit = defineEmits(["spinStopped"]);

const props = defineProps<{
    state: SpinAutomatState;
    winIcons: string[];
}>();

const isMovSupported = ResponsiveUtils.isMovSupported();
const isIos = ResponsiveUtils.isIos();
const isSafari = ResponsiveUtils.isSafari();

const defaultAnimationRef = ref<HTMLVideoElement>();
const rollingAnimationRef = ref<HTMLVideoElement>();
const winningAnimationRef = ref<HTMLVideoElement>();

const additionalClass = ref("");

const { session } = useNgSession();

const iconsUrls = ref<string[]>([]);
const init = async () => {
    iconsUrls.value = (await fetchAutomatCylinderImages(session.value?.PlayerID))?.icons ?? [];
    Loader.hide();
};

Loader.show();
init();

const showDefaultAnimation = computed(
    () => props.state != "ROLLING" && props.state != "STOPPING" && props.state != "WINNING"
);
const showRollingAnimation = computed(() => props.state == "ROLLING" || props.state == "STOPPING");
const showWinningAnimation = computed(() => props.state == "WINNING");

onMounted(() => {
    watchEffect(() => {
        if (showDefaultAnimation.value) {
            defaultAnimationRef.value.play();
            if (isIos || isSafari) {
                defaultAnimationRef.value.onloadeddata = () =>
                    setTimeout(() => {
                        additionalClass.value = "bonusomat-spin-automat--ios-loaded";
                    }, 0);
            }
        } else if (showRollingAnimation.value) {
            rollingAnimationRef.value.play();
        } else if (showWinningAnimation.value) {
            winningAnimationRef.value.play();
        }
    });
});

let stopCounter = 0;
const cylinderSpinEndHandler = () => {
    stopCounter++;
    if (props.winIcons.length == stopCounter) {
        emit("spinStopped");
        stopCounter = 0;
    }
};

const automatRollingMovSrc = bonImgUrl("automat/automat-rolling", "mov");
const automatRollingWebmSrc = bonImgUrl("automat/automat-rolling", "webm");
const automatWinMovSrc = bonImgUrl("automat/automat-win", "mov");
const automatWinWebmSrc = bonImgUrl("automat/automat-win", "webm");
const preloadVideos = () => {
    if (isMovSupported) {
        MediaPreloader.preloadVideos([automatRollingMovSrc, automatWinMovSrc]);
    } else {
        MediaPreloader.preloadVideos([automatRollingWebmSrc, automatWinWebmSrc]);
    }
};
watchEffect(() => {
    if (props.state == "ACTIVE") {
        preloadVideos();
    }
});
</script>
